import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { useBusiness } from '../context/BusinessContext';
import PageHeader from '../components/PageHeader';
import SignaturePad from '../components/SignaturePad';

const CLASS_OPTIONS = [
  { key: 'individual', label: 'Individual / sole proprietor or single-member LLC' },
  { key: 'c_corp', label: 'C Corporation' },
  { key: 's_corp', label: 'S Corporation' },
  { key: 'partnership', label: 'Partnership' },
  { key: 'trust_estate', label: 'Trust / estate' },
  { key: 'llc', label: 'Limited liability company' },
  { key: 'other', label: 'Other' },
];
const emptyForm = () => ({ tax_classification: '', llc_tax_class: 'S', other_classification: '', exempt_payee_code: '', fatca_code: '', signer_name: '', signer_title: '' });

function addrText(a) {
  let o = a;
  if (typeof a === 'string') { try { o = JSON.parse(a); } catch { return a; } }
  if (!o) return '';
  const city = [o.city, o.state].filter(Boolean).join(', ');
  return [o.line1, o.line2, [city, o.postal_code].filter(Boolean).join(' ').trim()].filter(Boolean).join(' · ');
}

export default function W9() {
  const { activeId, activeBusiness } = useBusiness();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [savedSig, setSavedSig] = useState(null);
  const [replacingSig, setReplacingSig] = useState(false);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  const sigRef = useRef(null);

  const [customers, setCustomers] = useState([]);
  const [sendTo, setSendTo] = useState({ customer_id: '', email: '', message: '' });
  const [sendResult, setSendResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [reviewComments, setReviewComments] = useState({});
  const [reviewing, setReviewing] = useState(null);

  const load = useCallback(() => {
    if (!activeId) return;
    api(`/w9/${activeId}`).then((d) => {
      setInfo(d);
      const w = d.w9 || {};
      setForm({ ...emptyForm(), ...['tax_classification', 'llc_tax_class', 'other_classification', 'exempt_payee_code', 'fatca_code', 'signer_name', 'signer_title'].reduce((o, k) => ({ ...o, [k]: w[k] || (k === 'llc_tax_class' ? 'S' : '') }), {}) });
      setSavedSig(w.signature_data || null);
      setReplacingSig(false);
    }).catch(() => setInfo(null));
    api(`/customers?business_id=${activeId}`).then((rows) => setCustomers((rows || []).filter((c) => c.is_active !== false))).catch(() => setCustomers([]));
  }, [activeId]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setSaving(true); setNotice(null);
    try {
      const body = { ...form };
      // The pad is only mounted when capturing (first-time or replacing); if it has ink, save it.
      if (sigRef.current && !sigRef.current.isEmpty()) body.signature_data = sigRef.current.toTrimmedDataURL();
      const d = await api(`/w9/${activeId}`, { method: 'PUT', body });
      setInfo((cur) => ({ ...cur, ...d }));
      if (body.signature_data) setSavedSig(body.signature_data);
      setReplacingSig(false);
      setNotice('Saved.');
    } catch (e) { setNotice(e?.detail || 'Could not save.'); }
    finally { setSaving(false); }
  }

  function preview(download) {
    const params = new URLSearchParams();
    if (download) params.set('download', '1');
    if (sendTo.customer_id) params.set('customer_id', sendTo.customer_id);
    const qs = params.toString();
    window.open(`/api/w9/${activeId}/pdf${qs ? `?${qs}` : ''}`, '_blank', 'noopener');
  }

  async function send() {
    setSending(true); setSendResult(null);
    try {
      const body = { customer_id: sendTo.customer_id || null, email: sendTo.email.trim() || null, message: sendTo.message.trim() || null };
      const r = await api(`/w9/${activeId}/send`, { method: 'POST', body });
      setSendResult({ ok: true, msg: `Sent to ${r.email}.` });
      setSendTo({ customer_id: '', email: '', message: '' });
      load();
    } catch (e) { setSendResult({ ok: false, msg: e?.detail || 'Could not send.' }); }
    finally { setSending(false); }
  }

  async function review(id, action) {
    setReviewing(id); setNotice(null);
    try {
      await api(`/w9/requests/${id}/${action}`, { method: 'POST', body: { comments: reviewComments[id] || null } });
      setReviewComments((c) => { const n = { ...c }; delete n[id]; return n; });
      load();
    } catch (e) { setNotice(e?.detail || 'Could not process the request.'); }
    finally { setReviewing(null); }
  }

  if (!info) return <div className="page"><PageHeader title="W-9 forms" subtitle="Loading…" /></div>;

  const { business, missing } = info;
  const ready = missing.length === 0;
  const pendingCount = (info.requests || []).filter((r) => r.status === 'pending').length;

  return (
    <div className="page">
      <PageHeader title="W-9 forms" subtitle={`Completed Form W-9 for ${activeBusiness?.name || business?.name}, ready to send when a customer requests it.`} />

      {notice && <div className="form-note">{notice}</div>}

      <div className="w9-grid">
        <section className="panel">
          <h3 className="form-section">Business details (from the business profile)</h3>
          <div className="w9-ro">
            <div><span className="w9-ro__k">Name (legal)</span><span className="w9-ro__v">{business.legal_name || business.name || '—'}</span></div>
            <div><span className="w9-ro__k">Business / DBA</span><span className="w9-ro__v">{business.name || '—'}</span></div>
            <div><span className="w9-ro__k">Address</span><span className="w9-ro__v">{addrText(business.address) || '—'}</span></div>
            <div><span className="w9-ro__k">Taxpayer ID (EIN/SSN)</span><span className="w9-ro__v">{business.tax_id || '— not set —'}</span></div>
          </div>
          <p className="hint">Name, address, and Taxpayer ID come from the business profile. Edit them under Business profile if they're wrong.</p>

          <h3 className="form-section">W-9 details</h3>
          <div className="field"><label>Federal tax classification</label>
            <select value={form.tax_classification} onChange={(e) => set('tax_classification', e.target.value)}>
              <option value="">— Select —</option>
              {CLASS_OPTIONS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          {form.tax_classification === 'llc' && (
            <div className="field"><label>LLC tax classification</label>
              <select value={form.llc_tax_class} onChange={(e) => set('llc_tax_class', e.target.value)}>
                <option value="C">C = C corporation</option>
                <option value="S">S = S corporation</option>
                <option value="P">P = Partnership</option>
              </select>
            </div>
          )}
          {form.tax_classification === 'other' && (
            <div className="field"><label>Describe</label><input value={form.other_classification} onChange={(e) => set('other_classification', e.target.value)} /></div>
          )}
          <div className="form-grid">
            <div className="field"><label>Exempt payee code (optional)</label><input value={form.exempt_payee_code} onChange={(e) => set('exempt_payee_code', e.target.value)} /></div>
            <div className="field"><label>FATCA exemption code (optional)</label><input value={form.fatca_code} onChange={(e) => set('fatca_code', e.target.value)} /></div>
          </div>
          <div className="form-grid">
            <div className="field"><label>Signer name</label><input value={form.signer_name} onChange={(e) => set('signer_name', e.target.value)} /></div>
            <div className="field"><label>Signer title (optional)</label><input value={form.signer_title} onChange={(e) => set('signer_title', e.target.value)} /></div>
          </div>

          <div className="field">
            <label>Signature</label>
            {savedSig && !replacingSig ? (
              <div className="w9-sig">
                <img src={savedSig} alt="Signature on file" className="w9-sig__img" />
                <button className="btn btn--sm" onClick={() => setReplacingSig(true)}>Replace signature</button>
              </div>
            ) : (
              <div className="w9-sig">
                <SignaturePad ref={sigRef} />
                <div className="w9-sig__acts">
                  <button className="btn btn--sm" onClick={() => sigRef.current?.clear()}>Clear</button>
                  {savedSig && <button className="btn btn--sm btn--ghost" onClick={() => setReplacingSig(false)}>Keep existing</button>}
                </div>
              </div>
            )}
          </div>

          <div className="form-section__act" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn--primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            <button className="btn" onClick={() => preview(false)}>Preview PDF</button>
            <button className="btn" onClick={() => preview(true)}>Download</button>
          </div>
        </section>

        <section className="panel">
          <h3 className="form-section">Send to a customer</h3>
          {!ready && (
            <div className="form-error">
              Finish the W-9 before sending. Still needed:
              <ul className="w9-missing">{missing.map((m) => <li key={m}>{m}</li>)}</ul>
            </div>
          )}
          <div className="field"><label>Customer</label>
            <select value={sendTo.customer_id} onChange={(e) => { const id = e.target.value; const c = customers.find((x) => String(x.id) === id); setSendTo((s) => ({ ...s, customer_id: id, email: c?.email || s.email })); }}>
              <option value="">— Choose a customer (optional) —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.email ? ` · ${c.email}` : ''}</option>)}
            </select>
          </div>
          <div className="field"><label>Send to email</label>
            <input type="email" placeholder="name@company.com" value={sendTo.email} onChange={(e) => setSendTo((s) => ({ ...s, email: e.target.value }))} /></div>
          <div className="field"><label>Message (optional)</label>
            <textarea rows={3} placeholder="Leave blank to use a standard cover note." value={sendTo.message} onChange={(e) => setSendTo((s) => ({ ...s, message: e.target.value }))} /></div>
          <button className="btn btn--primary btn--block" onClick={send} disabled={sending || !ready || (!sendTo.customer_id && !sendTo.email.trim())}>{sending ? 'Sending…' : 'Send W-9'}</button>
          {sendResult && <div className={sendResult.ok ? 'form-note' : 'form-error'} style={{ marginTop: 10 }}>{sendResult.msg}</div>}

          {info.sends?.length > 0 && (
            <>
              <h3 className="form-section">Recently sent</h3>
              <div className="w9-sends">
                {info.sends.map((s) => (
                  <div className="w9-send" key={s.id}>
                    <span className="w9-send__to">{s.customer_name || s.email}</span>
                    <span className="w9-send__meta">{new Date(s.sent_at).toLocaleDateString()}{s.sent_by_name ? ` · ${s.sent_by_name}` : ''}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      </div>

      {info.requests?.length > 0 && (
        <section className="panel" style={{ marginTop: 16 }}>
          <h3 className="form-section">Customer requests
            {pendingCount > 0 && <span className="w9-pendpill">{pendingCount} pending</span>}
          </h3>
          <div className="w9-reqs">
            {info.requests.map((r) => (
              <div className={'w9-req w9-req--' + r.status} key={r.id}>
                <div className="w9-req__main">
                  <div className="w9-req__top">
                    <span className="w9-req__cust">{r.customer_name}{r.customer_email ? ` · ${r.customer_email}` : ''}</span>
                    <span className={'w9-req__status w9-req__status--' + r.status}>{r.status}</span>
                  </div>
                  <div className="w9-req__meta">Requested {new Date(r.created_at).toLocaleString()}</div>
                  {r.request_note && <div className="w9-req__note">“{r.request_note}”</div>}
                  {r.status !== 'pending' && (
                    <div className="w9-req__meta">{r.status === 'approved' ? 'Approved' : 'Denied'}{r.reviewed_by_name ? ` by ${r.reviewed_by_name}` : ''}{r.reviewed_at ? ` · ${new Date(r.reviewed_at).toLocaleDateString()}` : ''}{r.decision_comments ? ` — ${r.decision_comments}` : ''}</div>
                  )}
                </div>
                {r.status === 'pending' && (
                  <div className="w9-req__act">
                    <input className="w9-req__comment" placeholder="Comments (optional)" value={reviewComments[r.id] || ''} onChange={(e) => setReviewComments((c) => ({ ...c, [r.id]: e.target.value }))} />
                    <div className="w9-req__btns">
                      <button className="btn btn--sm btn--primary" disabled={!ready || reviewing === r.id} onClick={() => review(r.id, 'approve')} title={ready ? 'Generate, store, and email the W-9' : 'Complete the W-9 first'}>Approve &amp; send</button>
                      <button className="btn btn--sm btn--danger" disabled={reviewing === r.id} onClick={() => review(r.id, 'deny')}>Deny</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          {!ready && info.requests.some((r) => r.status === 'pending') && <p className="form-error" style={{ marginTop: 10 }}>Complete the W-9 above before approving requests.</p>}
        </section>
      )}
    </div>
  );
}
